import { useRef } from "react"
import useComponentSize from "@rehooks/component-size"
import { observer } from "mobx-react-lite"

import { HideWhenFalse } from "@/components/conditionals"
import { ScrollArea } from "@/components/scroll-area"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { PlusCircleIcon } from "@/domains/icons"
import { cn, dollars } from "@/domains/library"
import { TokenStore } from "@/domains/tokens"
import { TokenIcon } from "@/domains/tokens/components/token-icon"

export const TokenList: React.FC<{
  customActions?: React.ReactNode
  maxHeight?: number
  showAddToWallet?: boolean
  showTitle?: boolean
  tokens: Array<TokenStore>
  onClose?: () => void
  onTokenSelect: (token: TokenStore) => void
}> = observer(
  ({ tokens, showTitle = true, onTokenSelect, showAddToWallet = false, customActions, onClose, maxHeight = 280 }) => {
    const ref = useRef<HTMLDivElement>(null)
    const refHeight = useComponentSize(ref)

    const tokenListHeight = Math.max(Math.min(maxHeight, refHeight.height), maxHeight)

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const ethereum =
      typeof window !== "undefined" ? (window as Window & { ethereum?: { isMetaMask?: boolean } }).ethereum : undefined

    const handleTokenSelect = (token: TokenStore) => {
      onTokenSelect(token)
      onClose?.()
    }

    const handleAddToWallet = async (token: TokenStore, event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()

      if (ethereum) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          await ethereum.request({
            method: "wallet_watchAsset",
            params: {
              type: "ERC20",
              options: {
                address: token.address.toViemAddress(),
                symbol: token.symbol,
                decimals: token.decimals,
                image: `https://cdn.jsdelivr.net/gh/unified-defi/equalizer-tokens@latest/${token.chainSlug}/tokens/128x128/${token.address.toViemAddress().toLowerCase()}.png`,
              },
            },
          })
        } catch (error) {
          console.error("Failed to add token to wallet:", error)
        }
      }
    }

    return (
      <div className="h-full flex-grow" ref={ref}>
        {showTitle ? <BodyCaption className="mb-1 ml-1 text-t200">Tokens</BodyCaption> : null}
        <ScrollArea
          className={cn("-mx-2 rounded-md border-primary pr-3", showTitle ? "pt-2" : "")}
          style={{ height: `${tokenListHeight}px` }}
        >
          <div className="flex flex-col items-center space-y-1">
            {tokens.map((token: TokenStore) => {
              return (
                <div
                  key={token.address.toViemAddress()}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-xl bg-transparent px-3 py-2 text-sm transition-colors hover:bg-p400-light/50"
                  onClick={() => handleTokenSelect(token)}
                >
                  <div className="flex h-[32px] w-[32px] flex-row items-center justify-between">
                    <TokenIcon token={token} className="" size={32} />
                  </div>
                  <div className="flex grow flex-col items-start normal-case">
                    <div className="text-sm text-t100">
                      {token.symbol ? token.symbol : "Not available"}{" "}
                      <span className="truncate text-t200">({token.name ? token.name : "🤷"})</span>
                    </div>
                    <div className="hidden text-xs text-t200 md:block">{token.address.toViemAddress()}</div>
                    <div className="block text-xs text-t200 md:hidden">{token.address.truncate(4, 4)}</div>
                  </div>
                  {customActions}
                  <div className="flex flex-col items-end font-normal  normal-case">
                    <div className="text-sm text-t100">{token.displayBalance}</div>
                    <div className="text-xs text-t200">~{dollars(token.balanceValueUSD)}</div>
                  </div>
                  <HideWhenFalse when={showAddToWallet && Boolean(ethereum)}>
                    <div className="">
                      <PlusCircleIcon
                        size={16}
                        className="ml-4 cursor-pointer text-t200"
                        onClick={(event) => handleAddToWallet(token, event)}
                      />
                    </div>
                  </HideWhenFalse>
                </div>
              )
            })}
          </div>
        </ScrollArea>
      </div>
    )
  },
)
